import { createContext, useContext, useState } from "react";

interface Reference {
  id: string;
  title: string;
  content: string;
}

interface ReferenceContextType {
  reference: Reference | null;
  setReference: (ref: Reference | null) => void;
  fetchReference: (id: string) => Promise<Reference>;
  isLoading: boolean;
}

export const ReferenceContext = createContext<ReferenceContextType | undefined>(
  undefined
);

export function ReferenceProvider({ children }: { children: React.ReactNode }) {
  const [reference, setReference] = useState<Reference | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchReference = async (id: string) => {
    setIsLoading(true);
    try {
      // TODO: Implement loading a reference. Currently simulating the API call with stubbed data
      const stubbedData: Reference = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            id,
            title:
              "Code of Ordinances City of Springdale, AR - Chapter 6 - Article 3 - Section 6.62",
            content: `Chapter 6 ALCOHOLIC BEVERAGES
ARTICLE III. DOWNTOWN SPRINGDALE OUTDOOR DINING DISTRICT

Sec. 6-62. Regulations.

(a) Restaurants, bars, and breweries, within the district boundaries, as well as vendors at special events within the district boundaries, may serve alcoholic beverages that may be carried out of the establishment and consumed within the designated boundaries of the district between the hours of:
    - 3:00 p.m. and 10:00 p.m. on Monday through Friday
    - 8:00 a.m. and 10:00 p.m. on Saturday
    - 10:00 a.m. and 10:00 p.m. on Sunday
    as long as the beverage is contained in a designated district cup provided by a participating business in the district.

(b) The Springdale Public Events Committee shall have the right, at its discretion, to exclude Walter Turnbow Park and/or Shiloh Square during specified times.

(c) The boundaries of the district will be designated by signage placed on the sidewalk within the district.

(d) As needed trash and recycling receptacles will be placed at the boundaries of the district.

(e) A document will be prepared showing the boundaries of the district, participating bars and restaurants, information regarding the regulations of the district, and frequently asked questions. The document will be available on Explore Springdale and Downtown Springdale digital platforms.

(f) Participating restaurants and bars may not allow alcoholic beverages to be removed from the premises in glass containers and it shall be unlawful for any person to possess alcoholic beverages in glass containers at Walter Turnbow Park and Shiloh Square, and on the streets, sidewalks, rights-of-way, and parking lots located within the district.

(g) No alcoholic beverages purchased outside the district shall be allowed in open containers in the district.

(h) Nothing in this ordinance shall be construed to allow any person to possess open containers of alcohol beverages or to consume alcoholic beverages in a motor vehicle.

(i) No person shall be permitted to remove an open alcoholic beverage container from the district. All unfinished quantities of alcoholic beverages must be properly disposed of prior to the individual leaving the district.

(Ord. No. 5498, § 2, 8-11-20; Ord. No. 5772, § 2, 10-11-22)`,
          });
        }, 500);
      });

      setReference(stubbedData);
      return stubbedData;
    } catch (error) {
      Honeybadger.notify(error);
      console.error("Failed to fetch reference:", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ReferenceContext.Provider
      value={{ reference, setReference, fetchReference, isLoading }}
    >
      {children}
    </ReferenceContext.Provider>
  );
}

export function useReference() {
  const context = useContext(ReferenceContext);
  if (context === undefined) {
    throw new Error("useReference must be used within a ReferenceProvider");
  }
  return context;
}
